/* ============================================================================
 * РЕПЛЕЙ v2 (08z): плеер записанной twin-сцены в консоли ИВО.
 *
 * Бэкенд готов (08t SS8.6): l3_orchestrator пишет кадры
 *   {t, session, snap{observations}, trajectories{trails,predict,conflicts}, events}
 * в ring-буфер cap 1200 (~60 мин при тике 3 с), ручки консоли:
 *   GET  /api/l3/replay_info   -> {ok,on,meta{t0,t1,sid,frames_written,dropped},frames}
 *   GET  /api/l3/replay_frame?i=N -> {ok,i,frame}
 *   GET  /api/l3/traffic/get?i=N  -> snap кадра (подменный трафик карты)
 *   POST /api/l3/replay        -> вкл/выкл записи (инструктор)
 * Фронтового плеера НЕ БЫЛО — этот компонент его добавляет.
 *
 * ДИЗАЙН ВСТРОЙКИ: НЕ дублируем модель данных. Плеер только ПЕРЕХВАТЫВАЕТ
 * входы карты App.tsx (points, geoPoints, траектории) и подменяет их кадрами
 * реплея. Модель цели = та же toTracks (trackModel) в App, ленты/дуги = те же
 * trailToFeature/predictToFeature (map/layers) — карта не знает, что показывает
 * запись. Лента событий twin = события из кадра (панель L3 — их живой поток).
 *
 * Роли (ivo_auth RULES): чтение кадров — l3_trainee+l3_instructor,
 * вкл/выкл записи — l3_instructor (кнопка «Запись» только у инструктора).
 * ========================================================================= */
import { useCallback, useEffect, useRef, useState } from 'react'
import { History, Pause, Play, SkipBack, SkipForward, Square } from 'lucide-react'
import { Button } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { request, json, TIMEOUT } from '../../lib/api'
import type { TrafficObservation, TrajSnapshot } from '../../types'

/** Наблюдение в кадре реплея (сырой формат FB из оркестратора) */
interface RawObs {
  icao_address?: string
  lat_dd?: number
  lon_dd?: number
  altitude_mm?: number
  asl_m?: number | null
  agl_m?: number | null
  env?: string
  traffic_source?: number
  metadata?: { callsign?: string; [k: string]: unknown }
  [k: string]: unknown
}

/** Кадр реплея (формат l3_orchestrator REC.add_frame) */
export interface ReplayFrame {
  t: number
  session?: string | null
  snap?: { observations?: RawObs[]; [k: string]: unknown }
  trajectories?: Partial<TrajSnapshot>
  events?: Array<{ kind: string; drone: string; ts: string; text?: string }>
}

export interface ReplayInfo {
  ok?: boolean
  on?: boolean
  meta?: { t0?: number; t1?: number; sid?: string | null; frames_written?: number; dropped?: number }
  frames?: number
}

/** Период опроса replay_info, мс */
const INFO_POLL_MS = 5000
/** Период кадров при воспроизведении (тик сцены 3 с -> честный темп 3 с/кадр) */
const FRAME_MS = 3000

/** Результат разбора кадра для подмены входов карты App.tsx */
export interface ReplayMapData {
  observations: TrafficObservation[]
  traj: TrajSnapshot | null
  events: Array<{ kind: string; drone: string; ts: string; text?: string }>
  t: number
}

/** Разобрать кадр в формат карты (поле за полем, без общего каста) */
export function frameToMapData(frame: ReplayFrame): ReplayMapData {
  const raw = frame.snap?.observations ?? []
  const observations: TrafficObservation[] = raw.map((o, i) => ({
    id: o.icao_address ?? `rep-${i}`,
    icao: o.icao_address,
    callsign: o.metadata?.callsign,
    lat: o.lat_dd,
    lon: o.lon_dd,
    alt_m: o.altitude_mm != null ? Number(o.altitude_mm) / 1000 : undefined,
    asl_m: o.asl_m ?? null,
    agl_m: o.agl_m ?? null,
    env: o.env,
    traffic_source: o.traffic_source,
  }))
  return {
    observations,
    traj: (frame.trajectories as TrajSnapshot | undefined) ?? null,
    events: frame.events ?? [],
    t: frame.t,
  }
}

/* ----------------------------------------------------------------- API */

export function fetchReplayInfo(signal?: AbortSignal): Promise<ReplayInfo> {
  return request<ReplayInfo>('/api/l3/replay_info', { signal }, TIMEOUT.action)
}

export function fetchReplayFrame(
  i: number,
  signal?: AbortSignal,
): Promise<{ ok: boolean; i: number; frame?: ReplayFrame }> {
  return request<{ ok: boolean; i: number; frame?: ReplayFrame }>(
    `/api/l3/replay_frame?i=${i}`,
    { signal },
    TIMEOUT.action,
  )
}

/** Вкл/выкл записи кадров (только инструктор, RULES) */
export function setReplayRecording(on: boolean): Promise<{ ok: boolean; on?: boolean; error?: string }> {
  return request('/api/l3/replay', json({ on }), TIMEOUT.action)
}

/* -------------------------------------------------------------- КОМПОНЕНТ */

interface ReplayPlayerProps {
  isInstructor: boolean
  /** Прокинуть разобранный кадр в App (подмена карты) или null = живой поток */
  onFrame: (data: ReplayMapData | null) => void
}

export function ReplayPlayer({ isInstructor, onFrame }: ReplayPlayerProps) {
  const [info, setInfo] = useState<ReplayInfo | null>(null)
  const [idx, setIdx] = useState<number>(-1) // -1 = живой поток
  const [playing, setPlaying] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const alive = useRef(true)
  const frameCache = useRef<Map<number, ReplayFrame>>(new Map())
  const onFrameRef = useRef(onFrame)
  onFrameRef.current = onFrame

  useEffect(() => {
    alive.current = true
    const ac = new AbortController()
    const tick = () => {
      fetchReplayInfo(ac.signal)
        .then((r) => {
          if (alive.current) setInfo(r)
        })
        .catch(() => {
          /* деградация: держим последнее */
        })
    }
    tick()
    const t = setInterval(tick, INFO_POLL_MS)
    return () => {
      alive.current = false
      ac.abort()
      clearInterval(t)
    }
  }, [])

  const total = info?.frames ?? 0

  const loadFrame = useCallback(
    async (i: number): Promise<ReplayFrame | null> => {
      if (i < 0 || i >= total) return null
      const c = frameCache.current.get(i)
      if (c) return c
      try {
        const r = await fetchReplayFrame(i)
        const f = r.frame ?? null
        if (f) {
          frameCache.current.set(i, f)
          if (frameCache.current.size > 1200) frameCache.current.clear()
        }
        return f
      } catch {
        return null
      }
    },
    [total],
  )

  /** Переход на кадр и прокидка в App */
  const showFrame = useCallback(
    async (i: number) => {
      if (i < 0) {
        setIdx(-1)
        onFrameRef.current(null)
        return
      }
      setIdx(i)
      const f = await loadFrame(i)
      if (f) onFrameRef.current(frameToMapData(f))
    },
    [loadFrame],
  )

  // Тик воспроизведения: +1 кадр каждые FRAME_MS (честный темп тика 3 с)
  useEffect(() => {
    if (!playing) return
    let cancelled = false
    const t = setInterval(() => {
      setIdx((cur) => {
        const next = cur + 1
        if (next >= total) {
          setPlaying(false)
          return cur
        }
        void loadFrame(next).then((f) => {
          if (!cancelled && f) onFrameRef.current(frameToMapData(f))
        })
        return next
      })
    }, FRAME_MS)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [playing, total, loadFrame])

  const play = () => {
    if (total <= 0) return
    setPlaying(true)
    if (idx < 0) void showFrame(total - 1) // старт с последнего записанного
  }
  const pause = () => setPlaying(false)
  const live = () => {
    setPlaying(false)
    void showFrame(-1)
  }
  const step = (d: number) => {
    setPlaying(false)
    void showFrame(Math.min(total - 1, Math.max(0, (idx < 0 ? total - 1 : idx) + d)))
  }

  const recToggle = async () => {
    setBusy(true)
    setErr(null)
    try {
      await setReplayRecording(!(info?.on ?? true))
      setInfo(await fetchReplayInfo())
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const on = info?.on ?? true
  const active = idx >= 0
  const fmt = (ts?: number) =>
    ts ? new Date(ts * 1000).toLocaleTimeString('ru-RU', { hour12: false }) : '—'

  return (
    <div className="flex flex-col gap-1.5 rounded border border-line px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <History size={16} aria-hidden />
        <span className="text-sm font-medium">Реплей</span>
        <StateBadge state={active ? 'active' : on ? 'pending' : 'warning'}>
          {active ? `КАДР ${idx + 1}/${total}` : on ? 'ЗАПИСЬ ИДЁТ' : 'ЗАПИСЬ ВЫКЛ'}
        </StateBadge>
        <span className="text-xs opacity-60">
          {info?.meta?.t0 ? `${fmt(info.meta.t0)} — ${fmt(info.meta.t1)}` : 'нет данных'}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <Button onClick={() => step(-1)} disabled={total === 0 || busy} title="Кадр назад">
            <SkipBack size={14} />
          </Button>
          {playing ? (
            <Button onClick={pause} disabled={busy} title="Пауза">
              <Pause size={14} />
            </Button>
          ) : (
            <Button onClick={play} disabled={total === 0 || busy} title="Воспроизведение">
              <Play size={14} />
            </Button>
          )}
          <Button onClick={() => step(1)} disabled={total === 0 || busy} title="Кадр вперёд">
            <SkipForward size={14} />
          </Button>
          <Button onClick={live} disabled={!active || busy} title="Вернуться к живой обстановке">
            <Square size={14} /> Живая
          </Button>
          {isInstructor && (
            <Button onClick={recToggle} disabled={busy} title="Вкл/выкл записи кадров">
              <Square size={14} /> {on ? 'Запись ВЫКЛ' : 'Запись ВКЛ'}
            </Button>
          )}
        </span>
      </div>
      {err && <div className="text-xs text-red-500">{err}</div>}
    </div>
  )
}
